import { BaseExercise } from "@domain/exercise/exercise";
import { observable } from "micro-observables";
import { Filter, FilterCategory } from "@domain/filters/filter";
import { isWordMatchingSearch, orderList } from "@utils/string-utils";
import { ExerciseSelectionManager } from "@domain/exercise/exercise-selection-manager";
import { exerciseManager } from "@core/service-container";

export class ExerciseFilterManager extends ExerciseSelectionManager {

	private readonly _filteredExercises = observable<BaseExercise[]>([]);
	filteredExercises = this._filteredExercises.readOnly();

	searchWord = observable<string>("");
	activeFilters = observable<FilterCategory<ExerciseFilter>[]>([]);

	constructor() {
		super();
		this.searchWord.subscribe(() => this.filterExercises());
		this.activeFilters.subscribe(() => this.filterExercises());
	}

	async fetchExercises(): Promise<void> {
		await exerciseManager.fetchExercises();
	}

	clearAllFiltersAndSearch(): void {
		this.searchWord.set("");
		this.activeFilters.set([]);
	}

	filterExercises(): void {
		let filteredExercises = exerciseManager.allExercises.get().filter(exercise => {
			for (const category of this.activeFilters.get()) {
				if (!this.exerciseMatchFilterCategory(exercise, category)) {
					return false;
				}
			}
			return true;
		});

		const search = this.searchWord.get();

		if (search) {
			filteredExercises = [
				...new Set(
					[
						filteredExercises.filter(exercise => isWordMatchingSearch(exercise.title, search)),
						filteredExercises.filter(exercise => isWordMatchingSearch(exercise.description || "", search)),
						filteredExercises.filter(exercise => exercise.tags.some(tag => isWordMatchingSearch(tag, search))),
					].flat(),
				),
			];
		}

		this._filteredExercises.set(orderList(filteredExercises, "title"));
	}

	private exerciseMatchFilterCategory(exercise: BaseExercise, filterCategory: FilterCategory<ExerciseFilter>): boolean {
		if (filterCategory.filters.length === 0) {
			return true;
		}

		for (const filter of filterCategory.filters) {
			if (filter.predicate(exercise)) {
				return true;
			}
		}

		return false;
	}
}

export interface ExerciseFilter extends Filter {
	predicate: (exercise: BaseExercise) => boolean;
}
